import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  debounce,
} from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
  GridValueGetter,
} from "@mui/x-data-grid-pro";
import { axiosWithAuth } from "../plugins/axios";
import { useNavigate } from "react-router-dom";
import { useRole } from "../lib/contexts/RoleContext";
import { getAPIUrlByRole } from "../lib/utils/config";
import { saveAs } from "file-saver";

interface EvaluationMetrics {
  programName: string;
  quizId: string;
  quizName: string;
  courseId: string;
  // version: Number;
  totalSubmissions: string;
  pendingSubmissions: string;
  failedSubmissions: string;
  aiSubmissionEvaluated: number;
  submissionEvaluated: number;
  manualGradingPercent: string;
  firstUpdatedAt: string;
  belowMinus10: number;
  minus10toMinus5: number;
  minus5to0: number;
  exact0: number;
  zeroTo5: number;
  fiveTo10: number;
  above10: number;
  ExactAccuracy: string;
  AccuracyWithin5: string;
  AccuracyWithin10: string;
  version?: Number;
  crawlLinks: string;
}

const ListOfQuizzes = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [overallMetricsData, setOverallMetricsData] = useState<
    EvaluationMetrics[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [overallAccuracy, setOverallAccuracy] = useState<string | null>(null);
  const [avgDeviation, setAvgDeviation] = useState<number | null>(null);
  const [maxDeviation, setMaxDeviation] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>();

  const navigate = useNavigate();

  // const apiUrl = process.env.REACT_APP_PIPELINE_API;

  const role = useRole();
  const apiUrl = getAPIUrlByRole(role);

  useEffect(() => {
    const fetchQuizzes = async () => {
      const filters =
        filterModel?.items.map((item) => ({
          column: item.field,
          value: item.value,
          operator: item.operator,
        })) || [];
      setError(null);
      try {
        const response = await axiosWithAuth(
          `${apiUrl}autoGrade/dashboard/listOfQuizzes`,
          {
            params: {
              page: page + 1,
              limit: rowsPerPage,
              sortColumn: sortModel[0]?.field || null,
              sortDirection: sortModel[0]?.sort || null,
              // filterColumn: filterModel?.items[0]?.field || null,
              // filterValue: filterModel?.items[0]?.value || null,
              // filterOperator: filterModel?.items[0]?.operator || null,
              filters: JSON.stringify(filters),
            },
          },
          "get"
        );
        setQuizzes(response.data?.groupedQuizzes);
        setTotalCount(response.data?.totalCount || 0);
      } catch (err) {
        setError("Failed to load quizzes");
      } finally {
        setLoading(false);
      }
    };

    fetchQuizzes();
  }, [apiUrl, page, rowsPerPage, sortModel, filterModel]);

  const debounceFetchQuizzes = useMemo(
    () =>
      debounce((filter) => {
        setFilterModel(filter);
      }, 1000),
    [filterModel]
  );

  useEffect(() => {
    const fetchOverallMetrics = async () => {
      setMetricsError(null);
      try {
        const response = await axiosWithAuth(
          `${apiUrl}autoGrade/dashboard/overallMetrics`,
          {},
          "get"
        );
        setOverallAccuracy(
          response.data?.overallMetrics?.overallAccuracy || "N/A"
        );
        setAvgDeviation(
          Math.floor(response.data?.overallMetrics?.averageDeviation) || 0
        );
        setMaxDeviation(
          Math.floor(response.data?.overallMetrics?.maxDeviation) || 0
        );
        setOverallMetricsData(response.data?.overallMetricsData);
      } catch (err) {
        setMetricsError("Failed to load overall metrics");
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchOverallMetrics();
  }, [apiUrl]);

  const exportEvaluations = () => {
    if (!overallMetricsData || overallMetricsData.length === 0) {
      console.warn("No data to export.");
      return;
    }

    const isPrism = role === "prism";
    const headers = [
      "Program Name",
      "ID",
      "Quiz Name",
      isPrism ? "Version" : "Cohort ID",
      "Total Submissions",
      "Pending Submissions",
      "Failed Submissions",
      "AI Evaluated Submissions",
      "Human Evaluated Submissions",
      "Manual Grading %",
      "Extract Links",
      "Evaluation Start Date",
      "Deviation <-10%",
      "Deviation -10% to -5%",
      "Deviation -5% to 0%",
      "Deviation 0%",
      "Deviation 0% to 5%",
      "Deviation 5% to 10%",
      "Deviation >10%",
      "Exact Accuracy",
      "Accuracy within 5%",
      "Accuracy within 10%",
    ];

    const csvRows = [
      headers.map((header) => `"${header}"`).join(","), // Add headers first with double quotes
      ...overallMetricsData.map((row) =>
        [
          row.programName,
          row.quizId,
          row.quizName,
          isPrism ? row.version : row.courseId,
          row.totalSubmissions,
          row.pendingSubmissions,
          row.failedSubmissions,
          row.aiSubmissionEvaluated,
          row.submissionEvaluated,
          row.manualGradingPercent,
          row.crawlLinks,
          row.firstUpdatedAt,
          row.belowMinus10,
          row.minus10toMinus5,
          row.minus5to0,
          row.exact0,
          row.zeroTo5,
          row.fiveTo10,
          row.above10,
          row.ExactAccuracy,
          row.AccuracyWithin5,
          row.AccuracyWithin10,
        ].map((value) => `"${String(value).replace(/"/g, '""')}"`)
      ),
    ];

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    saveAs(blob, "listOfQuizzes.csv");
  };

  // const columns: GridColDef[] = [
  //   {field: "programName",headerName: "Program Name", width: 400},
  //   {
  //     field: "quizId",
  //     headerName: "ID",
  //     width: 80,
  //     renderCell: (params: GridRenderCellParams) => (
  //       <Button
  //         color="primary"
  //         onClick={() =>
  //           navigate(`/performanceDashboard/listOfQuizzes/cohort/${params?.row?.courseId}/quiz/${params.value}/evaluations`)
  //         }
  //       >
  //         {params.value}
  //       </Button>
  //     ),
  //   },
  //   { field: "quizName", headerName: "Quiz Name", width: 250 },
  //   { field: "courseId", headerName: "Cohort ID", width: 100 },
  //   {field: "aiSubmissionEvaluated",headerName: "AI Evaluated Submissions" ,width: 180},
  //   { field: "submissionEvaluated", headerName: "Human Evaluated Submissions", width: 180 },
  //   { field: "belowMinus10", headerName: "Deviation <-10%", width: 150 },
  //   { field: "minus10toMinus5", headerName: "Deviation -10% to -5%", width: 160 },
  //   { field: "minus5to0", headerName: "Deviation -5% to 0%", width: 160 },
  //   { field: "exact0", headerName: "Deviation 0%", width: 130 },
  //   { field: "zeroTo5", headerName: "Deviation 0% to 5%", width: 160 },
  //   { field: "fiveTo10", headerName: "Deviation 5% to 10%", width: 160 },
  //   { field: "above10", headerName: "Deviation >10%", width: 160 },
  //   { field: "ExactAccuracy", headerName: "Exact Accuracy", width: 150 },
  //   { field: "AccuracyWithin5", headerName: "Accuracy within 5%", width: 170 },
  //   { field: "AccuracyWithin10", headerName: "Accuracy within 10%", width: 170 },
  // ];

  const columns: GridColDef[] = [
    {
      field: "programName",
      headerName: "Program Name",
      width: 400,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.programName ?? "-"}</>
      ),
    },
    {
      field: "quizId",
      headerName: "ID",
      width: 80,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          color="primary"
          onClick={() =>
            navigate(
              `/performanceDashboard/listOfQuizzes/cohort/${params?.row?.courseId}/quiz/${params?.value}/evaluations`
            )
          }
        >
          {params?.value ?? "-"}
        </Button>
      ),
    },
    {
      field: "quizName",
      headerName: "Quiz Name",
      width: 250,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.quizName ?? "-"}</>
      ),
    },
    {
      field: role === "prism" ? "version" : "courseId",
      headerName: role === "prism" ? "Version" : "Cohort ID",
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <>
          {role === "prism"
            ? params.row?.version ?? "-"
            : params.row?.courseId ?? "-"}
        </>
      ),
    },

    {
      field: "totalSubmissions",
      headerName: "Total Submissions",
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.totalSubmissions ?? "-"}</>
      ),
      filterable: false,
    },
    {
      field: "pendingSubmissions",
      headerName: "Pending Submissions",
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.pendingSubmissions ?? "-"}</>
      ),
      filterable: false,
    },
    {
      field: "failedSubmissions",
      headerName: "Failed Submissions",
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.failedSubmissions ?? "-"}</>
      ),
      filterable: false,
    },
    {
      field: "aiSubmissionEvaluated",
      headerName: "AI Evaluated Submissions",
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.aiSubmissionEvaluated ?? "-"}</>
      ),
      filterable: false,
    },
    {
      field: "submissionEvaluated",
      headerName: "Human Evaluated Submissions",
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.submissionEvaluated ?? "-"}</>
      ),
    },
    {
      field: "manualGradingPercent",
      headerName: "Manual Grading %",
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.manualGradingPercent ?? "-"}</>
      ),
      filterable: false,
    },
    {
      field: "crawlLinks",
      headerName: "Extract Links",
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.crawlLinks ?? "-"}</>
      ),
      filterable: false,
    },
    {
      field: "firstUpdatedAt",
      headerName: "Evaluation Start Date",
      width: 150,
      renderCell: (params: GridRenderCellParams) =>
        params.row?.firstUpdatedAt
          ? new Date(params.row?.firstUpdatedAt).toLocaleString()
          : "-",
      valueGetter: (params: GridValueGetter) => {
        return new Date(params as unknown as string).toLocaleString();
      },
      filterable: false,
    },
    {
      field: "belowMinus10",
      headerName: "Deviation <-10%",
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.belowMinus10 ?? "-"}</>
      ),
    },
    {
      field: "minus10toMinus5",
      headerName: "Deviation -10% to -5%",
      width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.minus10toMinus5 ?? "-"}</>
      ),
    },
    {
      field: "minus5to0",
      headerName: "Deviation -5% to 0%",
      width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.minus5to0 ?? "-"}</>
      ),
    },
    {
      field: "exact0",
      headerName: "Deviation 0%",
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.exact0 ?? "-"}</>
      ),
    },
    {
      field: "zeroTo5",
      headerName: "Deviation 0% to 5%",
      width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.zeroTo5 ?? "-"}</>
      ),
    },
    {
      field: "fiveTo10",
      headerName: "Deviation 5% to 10%",
      width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.fiveTo10 ?? "-"}</>
      ),
    },
    {
      field: "above10",
      headerName: "Deviation >10%",
      width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.above10 ?? "-"}</>
      ),
    },
    {
      field: "ExactAccuracy",
      headerName: "Exact Accuracy",
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.ExactAccuracy ?? "-"}</>
      ),
    },
    {
      field: "AccuracyWithin5",
      headerName: "Accuracy within 5%",
      width: 170,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.AccuracyWithin5 ?? "-"}</>
      ),
    },
    {
      field: "AccuracyWithin10",
      headerName: "Accuracy within 10%",
      width: 170,
      renderCell: (params: GridRenderCellParams) => (
        <>{params.row?.AccuracyWithin10 ?? "-"}</>
      ),
    },
  ];

  if (loading) {
    return (
      <Box mt={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom mb={4}>
        {role === "prism" ? "List of Assets" : "List of Quizzes"}
        {/* List of Quizzes */}
      </Typography>
      <Box justifyContent={"right"} display={"flex"}>
        <Button
          variant="outlined"
          sx={{ mr: 3 }}
          onClick={exportEvaluations}
          disabled={totalCount <= 0}
        >
          {"Export Dashboard"}
        </Button>
      </Box>

      {/* <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={4} width="100%">
          <Box display="flex" alignItems="center" gap={1} flex={1}>
            <Typography fontWeight="bold">Overall Accuracy:</Typography>
            <Typography>{overallAccuracy}</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1} flex={1}>
            <Typography fontWeight="bold">Avg. Deviation:</Typography>
            <Typography>{avgDeviation}%</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1} flex={1}>
            <Typography fontWeight="bold">Highest Deviation:</Typography>
            <Typography>{maxDeviation}%</Typography>
          </Box>
        </Box>
      </Box>
      <Box display="flex" alignItems="center" gap={1} mb={5}>
        <Typography fontWeight="bold">Overall Accuracy (With ±10%):</Typography>
        <Typography>{overallAccuracy}</Typography>
      </Box> */}

      <Box mt={4}>
        <DataGridPro
          rows={quizzes}
          columns={columns}
          getRowId={(row) => row.quizId}
          rowHeight={70}
          paginationMode="server"
          sortingMode="server"
          pagination
          paginationModel={{
            page: page,
            pageSize: rowsPerPage,
          }}
          rowCount={totalCount}
          onPaginationModelChange={(newPaginationModel) => {
            setPage(newPaginationModel.page);
            setRowsPerPage(newPaginationModel.pageSize);
          }}
          pageSizeOptions={[5, 10, 25, 50]}
          filterMode="server"
          onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
          onFilterModelChange={(filter) => debounceFetchQuizzes(filter)}
        />
      </Box>
    </Box>
  );
};

export default ListOfQuizzes;
